import { z } from "zod";
import { PaginationQuerySchema } from "./common.js";
import { ContactEmailDeliverabilityStatusSchema } from "./contacts.js";
import { CrmLifecycleStageSchema, CrmMarketingConsentSchema } from "./crm-activity.js";
export const CrmCampaignStatusValues = ["draft", "sending", "sent", "failed"];
export const CrmCampaignChannelValues = ["email"];
export const CrmCampaignAudienceTypeValues = ["contact_group", "dynamic_segment"];
export const CrmCampaignSuppressionReasonValues = [
    "email_frequency_none",
    "email_bounced",
    "marketing_opted_out",
    "marketing_unknown",
    "missing_email",
];
export const CrmCampaignStatusSchema = z.enum(CrmCampaignStatusValues);
export const CrmCampaignChannelSchema = z.enum(CrmCampaignChannelValues);
export const CrmCampaignAudienceTypeSchema = z.enum(CrmCampaignAudienceTypeValues);
export const CrmCampaignSuppressionReasonSchema = z.enum(CrmCampaignSuppressionReasonValues);
export const CrmSegmentCriteriaSchema = z.object({
    search: z.string().trim().max(200).nullable().optional(),
    tags: z.array(z.string().trim().min(1).max(50)).max(20).default([]),
    lifecycleStages: z.array(CrmLifecycleStageSchema).max(20).default([]),
    marketingConsent: z.array(CrmMarketingConsentSchema).max(20).default([]),
    emailFrequencies: z
        .array(z.enum(["all", "weekly", "monthly", "none"]))
        .max(10)
        .default([]),
    ownerEmail: z.string().trim().email().nullable().optional(),
    source: z.string().trim().max(80).nullable().optional(),
    companyId: z.string().uuid().nullable().optional(),
    isDecisionMaker: z.boolean().nullable().optional(),
    lastActivityWithinDays: z.number().int().min(1).max(3650).nullable().optional(),
});
export const CrmCampaignMetricsSchema = z.object({
    audienceCount: z.number().int().nonnegative().default(0),
    sendableCount: z.number().int().nonnegative().default(0),
    suppressedCount: z.number().int().nonnegative().default(0),
    sentCount: z.number().int().nonnegative().default(0),
    failedCount: z.number().int().nonnegative().default(0),
    bounceCount: z.number().int().nonnegative().default(0),
    uniqueOpenCount: z.number().int().nonnegative().default(0),
    uniqueClickCount: z.number().int().nonnegative().default(0),
    bounceRate: z.number().nonnegative().max(1).default(0),
    openRate: z.number().nonnegative().max(1).default(0),
    clickRate: z.number().nonnegative().max(1).default(0),
    latestEngagementAt: z.string().datetime().nullable().default(null),
    trackedEmailId: z.string().uuid().nullable().default(null),
});
export const CrmCampaignSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(160),
    source: z.string().min(1).max(80),
    channel: CrmCampaignChannelSchema,
    status: CrmCampaignStatusSchema,
    audienceType: CrmCampaignAudienceTypeSchema.default("contact_group"),
    audienceGroupId: z.string().uuid().nullable(),
    audienceGroupName: z.string().nullable().default(null),
    audienceSegmentId: z.string().uuid().nullable().default(null),
    audienceSegmentName: z.string().nullable().default(null),
    subject: z.string().min(1).max(500),
    bodyHtml: z.string().min(1),
    senderEmail: z.string().email().nullable().default(null),
    scheduledAt: z.string().datetime().nullable().default(null),
    sentAt: z.string().datetime().nullable().default(null),
    trackedEmailId: z.string().uuid().nullable().default(null),
    metrics: CrmCampaignMetricsSchema,
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
const CrmCampaignMutationBaseSchema = z.object({
    name: z.string().min(1).max(160),
    source: z.string().min(1).max(80).default("campaign"),
    audienceGroupId: z.string().uuid().nullable().optional(),
    audienceSegmentId: z.string().uuid().nullable().optional(),
    subject: z.string().min(1).max(500),
    bodyHtml: z.string().min(1),
    scheduledAt: z.string().datetime().nullable().optional(),
});
export const CrmCampaignCreateSchema = CrmCampaignMutationBaseSchema.superRefine((data, ctx) => {
    const hasGroup = Boolean(data.audienceGroupId);
    const hasSegment = Boolean(data.audienceSegmentId);
    if (hasGroup === hasSegment) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Select exactly one audience: contact group or dynamic segment.",
            path: ["audienceGroupId"],
        });
    }
});
export const CrmCampaignUpdateSchema = CrmCampaignMutationBaseSchema.partial().superRefine((data, ctx) => {
    const hasGroup = Boolean(data.audienceGroupId);
    const hasSegment = Boolean(data.audienceSegmentId);
    const touchedAudience = data.audienceGroupId !== undefined || data.audienceSegmentId !== undefined;
    if (touchedAudience && hasGroup === hasSegment) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Select exactly one audience: contact group or dynamic segment.",
            path: ["audienceGroupId"],
        });
    }
});
export const CrmCampaignListQuerySchema = PaginationQuerySchema.extend({
    status: CrmCampaignStatusSchema.optional(),
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
});
export const CrmCampaignListResponseSchema = z.object({
    campaigns: z.array(CrmCampaignSchema),
    total: z.number().int().nonnegative(),
});
export const CrmEmailTemplateSchema = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(160),
    purpose: z.string().trim().min(1).max(80).default("campaign"),
    subject: z.string().trim().min(1).max(500),
    bodyHtml: z.string().min(1),
    version: z.number().int().positive(),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CrmEmailTemplateCreateSchema = z.object({
    name: z.string().trim().min(1).max(160),
    purpose: z.string().trim().min(1).max(80).default("campaign"),
    subject: z.string().trim().min(1).max(500),
    bodyHtml: z.string().min(1),
});
export const CrmEmailTemplateUpdateSchema = CrmEmailTemplateCreateSchema.partial();
export const CrmEmailTemplateListQuerySchema = PaginationQuerySchema.extend({
    purpose: z.string().trim().min(1).max(80).optional(),
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
});
export const CrmEmailTemplateListResponseSchema = z.object({
    templates: z.array(CrmEmailTemplateSchema),
    total: z.number().int().nonnegative(),
});
export const CrmEmailTemplateParamSchema = z.object({
    id: z.string().uuid(),
});
export const CrmSegmentSchema = z.object({
    id: z.string().uuid(),
    name: z.string().trim().min(1).max(160),
    description: z.string().nullable().default(null),
    criteria: CrmSegmentCriteriaSchema,
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const CrmSegmentCreateSchema = z.object({
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1000).nullable().optional(),
    criteria: CrmSegmentCriteriaSchema.default({}),
});
export const CrmSegmentUpdateSchema = CrmSegmentCreateSchema.partial();
export const CrmSegmentListQuerySchema = PaginationQuerySchema.extend({
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
});
export const CrmSegmentListResponseSchema = z.object({
    segments: z.array(CrmSegmentSchema),
    total: z.number().int().nonnegative(),
});
export const CrmSegmentParamSchema = z.object({
    id: z.string().uuid(),
});
export const CrmCampaignAudienceContactSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string().min(1).max(100),
    lastName: z.string().min(1).max(100),
    marketingConsent: CrmMarketingConsentSchema,
    emailFrequency: z.enum(["all", "weekly", "monthly", "none"]),
    emailDeliverabilityStatus: ContactEmailDeliverabilityStatusSchema.default("unknown"),
});
export const CrmCampaignSuppressedContactSchema = CrmCampaignAudienceContactSchema.extend({
    reason: CrmCampaignSuppressionReasonSchema,
});
export const CrmCampaignSuppressionSummarySchema = z.object({
    reason: CrmCampaignSuppressionReasonSchema,
    count: z.number().int().nonnegative(),
});
export const CrmCampaignAudiencePreviewSchema = z.object({
    campaignId: z.string().uuid().nullable().default(null),
    audienceType: CrmCampaignAudienceTypeSchema.default("contact_group"),
    audienceGroupId: z.string().uuid().nullable().default(null),
    audienceGroupName: z.string().nullable().default(null),
    audienceSegmentId: z.string().uuid().nullable().default(null),
    audienceSegmentName: z.string().nullable().default(null),
    totalCount: z.number().int().nonnegative(),
    sendableCount: z.number().int().nonnegative(),
    suppressedCount: z.number().int().nonnegative(),
    sendable: z.array(CrmCampaignAudienceContactSchema),
    suppressed: z.array(CrmCampaignSuppressedContactSchema),
    suppressionReasons: z.array(CrmCampaignSuppressionSummarySchema),
});
export const CrmSegmentPreviewResponseSchema = z.object({
    segment: CrmSegmentSchema,
    preview: CrmCampaignAudiencePreviewSchema,
});
export const CrmCampaignParamSchema = z.object({
    id: z.string().uuid(),
});
//# sourceMappingURL=crm-campaigns.js.map